<template>
<div class="simple-page">
  <HeadNav selectedLanguage="en" page="terms" />

  <main class="simple-page__main">
    <h1>Terms of Use</h1>

    <p><strong>Effective Date:</strong> September 11, 2026</p>
    <p><strong>Last Updated:</strong> September 14, 2026</p>

    <p>
      Welcome to <strong>Secure VPN</strong>. By accessing, browsing, or using this website, you agree to be bound by
      these Terms of Use, our Privacy Policy, and all applicable laws and regulations. If you do not agree with any part
      of these terms, please discontinue using this website.
    </p>

    <section>
      <h2>1. Educational & Informational Purpose</h2>
      <p>
        The content provided on this website—including articles, guides, protocol comparisons, and security insights—is
        for general informational and educational purposes only. We aim to help users understand online security, data
        encryption, and privacy technologies.
      </p>
    </section>

    <section>
      <h2>2. Official Distribution & App Downloads</h2>
      <p>
        Our website provides links and buttons that redirect users to official app marketplaces, primarily the
        <strong>Google Play Store</strong>, where users can safely download and install the Secure VPN application.
      </p>
      <ul>
        <li><strong>Official Channels:</strong> Users are strongly advised to download the Secure VPN application solely through official app store links provided on our website.</li>
        <li><strong>Store Agreements:</strong> Downloading and using the application through the Google Play Store implies full compliance with both <strong>Google Play's Terms of Service</strong> and our End-User License Agreement (EULA).</li>
        <li><strong>No Direct APK Installations:</strong> We do not require or encourage users to bypass official store permissions or install unverified third-party executable files.</li>
      </ul>
    </section>

    <section>
      <h2>3. Acceptable Use Policy & User Responsibilities</h2>
      <p>You agree to use this website and our services only for lawful purposes. You are strictly prohibited from using this website or the Secure VPN service to:</p>
      <ul>
        <li>Engage in any illegal, fraudulent, or unauthorized activities under local and international laws.</li>
        <li>Infringe upon the intellectual property, privacy, or legal rights of others.</li>
        <li>Distribute malware, viruses, spyware, or malicious code.</li>
        <li>Attempt unauthorized access to our servers, infrastructure, or connected networks.</li>
        <li>Conduct network attacks, automated abuse, or disrupt the availability of our services.</li>
      </ul>
      <p>
        Users are solely responsible for ensuring that their network activities comply with all applicable local
        regulations and network usage policies.
      </p>
    </section>

    <section>
      <h2>4. Intellectual Property & Third-Party Trademarks</h2>
      <p>
        All original content, branding, logos, graphics, and layout on this website are the intellectual property of
        <strong>Secure VPN</strong> and protected by applicable copyright laws.
      </p>
      <ul>
        <li><strong>Third-Party Trademarks:</strong> All third-party product names, logos, brands, and trademarks mentioned on this website (such as Google Play, Android, Windows, macOS) belong to their respective owners. Their reference is used solely for compatibility identification and educational description, and does not imply sponsorship, endorsement, or affiliation.</li>
      </ul>
    </section>

    <section>
      <h2>5. Content Accuracy & Disclaimers</h2>
      <p>
        While we endeavor to keep the information on this website accurate and up-to-date, we make no representations or
        warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, or availability of
        the website content or services.
      </p>
      <p>
        Network availability, speeds, and server locations provided by the application may vary based on user location,
        network conditions, and device capability.
      </p>
    </section>

    <section>
      <h2>6. Third-Party Links & External Services</h2>
      <p>
        This website may contain links to third-party websites or services (including external analytics and store
        platforms). We do not control, endorse, or assume responsibility for the content, privacy policies, or practices
        of any third-party websites.
      </p>
    </section>

    <section>
      <h2>7. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Secure VPN, its affiliates, and partners shall not be liable for any
        direct, indirect, incidental, or consequential damages resulting from your access to, or inability to access, this
        website or its services.
      </p>
    </section>

    <section>
      <h2>8. Modifications to Terms</h2>
      <p>
        We reserve the right to update or modify these Terms of Use at any time without prior notice. Continued use of the
        website following any changes constitutes your acceptance of the new Terms of Use.
      </p>
    </section>

    <section>
      <h2>9. Contact Us</h2>
      <p>If you have any questions or inquiries regarding these Terms of Use, please contact us at:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:support@secureproxyvpn.com">support@secureproxyvpn.com</a></li>
      </ul>
    </section>
  </main>
</div>
</template>

<script>
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
  name: 'TermsOfUse',
  components: {
    HeadNav,
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('vpn-site');
    }
  },
};
</script>

<style lang="scss" scoped>
.simple-page {
  min-height: 70vh;
  background: #fff;
}

.simple-page__main {
  width: var(--vpn-content-width);
  margin: 0 auto;
  padding: 56px 0 72px;
  color: var(--vpn-ink-muted, #5b6475);
}

h1 {
  margin: 0 0 32px;
  color: var(--vpn-ink, #1f2430);
  font-size: 42px;
  line-height: 1.15;
  font-weight: 800;
}

section {
  margin: 0 0 34px;
}

h2 {
  margin: 0 0 14px;
  color: var(--vpn-ink, #1f2430);
  font-size: 26px;
  line-height: 1.25;
  font-weight: 800;
}

p {
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 1.75;
}

ul {
  margin: 0 0 18px 22px;
  padding: 0;
}

li {
  margin: 0 0 8px;
  font-size: 16px;
  line-height: 1.65;
}

a {
  color: var(--vpn-brand, #6d4aff);
  font-weight: 700;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .simple-page__main {
    width: 100%;
    padding: 40px 16px 56px;
    box-sizing: border-box;
  }

  h1 {
    font-size: 34px;
  }

  h2 {
    font-size: 23px;
  }
}
</style>
